import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { structureApi, type PositionDetail } from "../../api/structure";
import type { AttributeValue } from "../../api/catalog";
import type { ApiError } from "../../api/client";
import { detailsMap, formatApiError } from "../../lib/apiError";
import { useToast } from "../../components/Toast";
import { AttributeValuesForm, compactAttributeValues } from "../../components/AttributeValuesForm";
import { AttributeValuesView } from "../../components/AttributeValuesView";
import Ic from "../../components/icons.jsx";
import { toRequest } from "./shared";

/** "Očekivano" + "Referentne vrednosti" — only for kinds that hold an asset (the page decides). */
export function ReferenceValuesSection({
  detail, canEdit, onSaved,
}: {
  detail: PositionDetail;
  canEdit: boolean;
  onSaved: (saved: PositionDetail) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [refValues, setRefValues] = useState<Record<string, AttributeValue>>({ ...detail.referenceValues });
  const [savingRefs, setSavingRefs] = useState(false);
  // Reference values are read-only until "Izmeni" (nothing changes by accident).
  const [editingRefs, setEditingRefs] = useState(false);

  // Every freshly loaded card (another position, or the same one after a save / mount) starts read-only.
  useEffect(() => {
    setRefValues({ ...detail.referenceValues });
    setEditingRefs(false);
  }, [detail]);

  async function saveReferenceValues() {
    setSavingRefs(true);
    try {
      const saved = await structureApi.update(detail.id, {
        ...toRequest(detail),
        referenceValues: compactAttributeValues(refValues),
      });
      onSaved(saved);
      setEditingRefs(false);
      toast.success(t("structure.toast.refValuesSaved", "Referentne vrednosti sačuvane."));
    } catch (err) {
      const ae = err as ApiError;
      const key = detailsMap(ae)?.key;
      const label = key ? (detail.attributeDefs.find((d) => d.key === key)?.label ?? key) : "";
      if (ae.code === "UNKNOWN_ATTRIBUTE") toast.error(t("assetTypes.error.unknownAttribute", "Nepoznat atribut: {{key}}", { key: label }));
      else if (ae.code === "INVALID_ATTRIBUTE_VALUE") toast.error(t("assetTypes.error.invalidAttributeValue", "Neispravna vrednost atributa: {{key}}", { key: label }));
      else toast.error(formatApiError(ae));
    } finally {
      setSavingRefs(false);
    }
  }

  return (
    <>
      {/* Expected type */}
      <section className="card">
        <div className="card-h"><h3>{t("structure.section.expected", "Očekivano")}</h3></div>
        <div className="card-b">
          <dl className="kv">
            <dt>{t("structure.field.expectedType", "Očekivani tip")}</dt>
            <dd>
              {detail.expectedTypeId ? (
                <Link to={`/tipovi/${detail.expectedTypeId}`}>
                  <span style={{ fontFamily: "var(--mono)", fontSize: 12.5 }}>{detail.expectedTypeCode}</span> {detail.expectedTypeName}
                </Link>
              ) : <span className="muted">—</span>}
            </dd>
          </dl>
          {!detail.expectedTypeId && (
            <p className="muted" style={{ fontSize: 12.5, margin: "10px 0 0" }}>
              {t("structure.expectedHint", "Bez očekivanog tipa montaža nudi sve assete, a referentne vrednosti nisu dostupne.")}
            </p>
          )}
        </div>
      </section>

      {/* Reference values */}
      <section className="card">
        <div className="card-h">
          <div>
            <h3>{t("structure.section.refValues", "Referentne vrednosti")}</h3>
            <div className="sub">{t("structure.refValuesSub", "Normalne radne vrednosti na ovom mestu, prema atributima očekivanog tipa.")}</div>
          </div>
          {canEdit && detail.attributeDefs.length > 0 && !editingRefs && (
            <button type="button" className="btn sm" onClick={() => { setRefValues({ ...detail.referenceValues }); setEditingRefs(true); }}>
              <Ic.pencil /> {t("action.edit", "Izmeni")}
            </button>
          )}
        </div>
        {detail.attributeDefs.length === 0 ? (
          <div className="empty">{t("structure.refValuesEmpty", "Nema definisanih atributa — zadajte očekivani tip koji ima atribute.")}</div>
        ) : !editingRefs ? (
          <div className="card-b">
            <AttributeValuesView defs={detail.attributeDefs} values={detail.referenceValues} />
          </div>
        ) : (
          <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <AttributeValuesForm defs={detail.attributeDefs} values={refValues} disabled={!canEdit} onChange={setRefValues} />
            <div className="form-actions">
              <button type="button" className="btn" disabled={savingRefs} onClick={() => { setRefValues({ ...detail.referenceValues }); setEditingRefs(false); }}>
                {t("action.cancel", "Otkaži")}
              </button>
              <button type="button" className="btn primary" disabled={savingRefs} onClick={saveReferenceValues}>
                {savingRefs ? t("action.saving", "Čuvanje…") : t("structure.saveRefValues", "Sačuvaj vrednosti")}
              </button>
            </div>
          </div>
        )}
      </section>
    </>
  );
}
